import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useCreateActivity } from '@/hooks/use-activities';
import { useProject } from '@/hooks/use-projects';
import { getLocalizedText } from '@/lib/utils';
import { createActivitySchema, type ActivityFormData } from '@fame/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

export function NewActivityPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: project } = useProject(projectId!);
  const createActivity = useCreateActivity();

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(createActivitySchema) as Resolver<ActivityFormData>,
    defaultValues: {
      projectId: projectId!,
      internalName: '',
      code: 0,
      price: 0,
      isActive: true,
    },
  });

  const onSubmit = async (data: ActivityFormData) => {
    await createActivity.mutateAsync(data);
    navigate(`/projecten/${projectId}`);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/projecten/${projectId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Nieuwe Activiteit</h1>
          {project && <p className="text-muted-foreground">{getLocalizedText(project.name)}</p>}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Activiteit Informatie</CardTitle>
          <CardDescription>Voeg een nieuwe optionele activiteit toe aan dit project</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="internalName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interne Naam</FormLabel>
                    <FormControl>
                      <Input placeholder="bijv. Museum Visit" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" step="1" placeholder="bijv. 101" {...field} />
                    </FormControl>
                    <FormDescription>Unieke code voor dit project</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prijs</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0,00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Actief</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={`/projecten/${projectId}`}>Annuleren</Link>
                </Button>
                <Button type="submit" disabled={createActivity.isPending}>
                  {createActivity.isPending ? 'Bezig met aanmaken...' : 'Activiteit Aanmaken'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
